<template>
  <v-card
    class="ma-0 px-1 app-form-page"
    :class="{
      'app-form-page--fixed': !isEmbedded && !isMobile,
      'app-form-page--mobile': isMobile && !isEmbedded,
      'app-form-page--embedded': isEmbedded,
    }"
    width="100%"
    flat
  >
    <!-- Mobile header (no tabs / no documents) -->
    <div v-if="isMobile && !isEmbedded" class="amf-header">
      <div class="amf-header__bar">
        <div class="amf-header__bar-actions">
          <button
            v-if="id"
            type="button"
            class="amf-header__btn amf-header__btn--create"
            :aria-label="$t(type)"
            @click="goTo(type)"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            v-if="!formType"
            type="button"
            class="amf-header__btn"
            :aria-label="$t(type + 'List')"
            @click="goTo(type + 'List')"
          >
            <v-icon size="20" color="#37474F">mdi-format-list-numbered</v-icon>
          </button>
        </div>
        <h1 class="amf-header__title">{{ pageTitle }}</h1>
        <button
          v-if="!formType"
          type="button"
          class="amf-header__btn amf-header__btn--back"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
        <span v-else class="amf-header__btn amf-header__btn--back" style="visibility: hidden" />
      </div>
    </div>

    <!-- Desktop / embedded toolbar -->
    <v-app-bar
      v-else
      flat
      class="my-0 app-form-toolbar"
      :class="
        isMobile
          ? 'mb-2 py-1 px-2 app-form-toolbar--mobile'
          : 'mb-0 py-0 px-2'
      "
      dense
      :height="!isEmbedded && !isMobile ? 40 : undefined"
    >
      <v-toolbar-title class="app-form-toolbar__title me-3">
        {{ pageTitle }}
      </v-toolbar-title>
      <v-checkbox
        v-model="status"
        class="mt-0 mx-2"
        dense
        hide-details
        :label="$t('status') + '  :  ' + (status ? $t('active') : $t('deactive'))"
        value="active"
      />
      <v-spacer />
      <div class="app-form-toolbar__actions">
        <v-tooltip v-if="id" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type)"
            >
              <v-icon color="teal"> mdi-plus-box-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type) }}</span>
        </v-tooltip>
        <v-tooltip v-if="!formType" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="goTo(type + 'List')"
            >
              <v-icon color="primary"> mdi-format-list-numbered </v-icon>
            </v-btn>
          </template>
          <span>{{ $t(type + "List") }}</span>
        </v-tooltip>
        <v-tooltip v-if="!formType" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              small
              class="app-form-toolbar__btn"
              v-on="on"
              @click="$router.go(-1)"
            >
              <v-icon color="primary"> mdi-arrow-left </v-icon>
            </v-btn>
          </template>
          <span>{{ $t("back") }}</span>
        </v-tooltip>
      </div>
    </v-app-bar>

    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />

    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="app-form-page__form"
      lazy-validation
    >
      <!-- Mobile summary (no tabs / no documents) -->
      <div v-if="isMobile && !isEmbedded" class="amf-summary" dir="rtl">
        <div class="amf-summary__status">
          <div
            class="amf-summary__chip"
            :class="status ? 'amf-summary__chip--green' : 'amf-summary__chip--pink'"
          >
            {{ $t("status") }} : {{ status ? $t("active") : $t("deactive") }}
          </div>
        </div>

        <button type="button" class="amf-summary__row" @click="nameSheet = true">
          <span class="amf-summary__label">{{ nameLabel }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !name }"
          >{{ name || "—" }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <template v-if="type === 'bank'">
          <button type="button" class="amf-summary__row" @click="branchSheet = true">
            <span class="amf-summary__label">{{ $t("branch") }}</span>
            <span
              class="amf-summary__value"
              :class="{ 'amf-summary__value--empty': !branch }"
            >{{ branch || "—" }}</span>
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
          <button type="button" class="amf-summary__row" @click="numberSheet = true">
            <span class="amf-summary__label">{{ $t("number") + " " + $t("account") }}</span>
            <span
              class="amf-summary__value"
              :class="{ 'amf-summary__value--empty': !number }"
            >{{ number || "—" }}</span>
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
          <button type="button" class="amf-summary__row" @click="dateSheet = true">
            <span class="amf-summary__label">{{ $t("Account opening date") }}</span>
            <span
              class="amf-summary__value"
              :class="{ 'amf-summary__value--empty': !date }"
            >{{ date || "—" }}</span>
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </template>

        <button
          type="button"
          class="amf-summary__row"
          :disabled="!!(id && !$store.state.isMainYear)"
          @click="openMobileAutocomplete('refCurrency')"
        >
          <span class="amf-summary__label">{{ $t("currency") }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !currency }"
          >{{ mobileCurrencyText }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>
        <button type="button" class="amf-summary__row" @click="openRateNumpad">
          <span class="amf-summary__label">{{ $t("currency-rate") }}</span>
          <span class="amf-summary__value">{{ mobileRateText }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>
        <button type="button" class="amf-summary__row" @click="openInitialNumpad">
          <span class="amf-summary__label">{{ $t("initialPrice") }}</span>
          <span class="amf-summary__value">{{ mobileInitialText }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          v-if="type === 'petty'"
          type="button"
          class="amf-summary__row"
          @click="openMobileAutocomplete('refPettyPerson')"
        >
          <span class="amf-summary__label">{{ $t("pettyPerson") }}</span>
          <span
            class="amf-summary__value"
            :class="{ 'amf-summary__value--empty': !mobilePettyPersonText }"
          >{{ mobilePettyPersonText || "—" }}</span>
          <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          type="button"
          class="amf-summary__row amf-summary__row--tall"
          @click="otherSheet = true"
        >
          <span class="amf-summary__label">{{ $t("otherInputs") }}</span>
          <span class="amf-summary__value amf-summary__value--muted">{{
            $t("other-inputs-hint")
          }}</span>
          <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <button
          v-if="type === 'cash'"
          type="button"
          class="amf-summary__row amf-summary__row--tall"
          @click="posSheet = true"
        >
          <span class="amf-summary__label">{{ $t("newPOS") }}</span>
          <span class="amf-summary__value amf-summary__value--muted">{{
            posItems && posItems.length
              ? posItems.length + " POS"
              : $t("no-data-found")
          }}</span>
          <v-icon size="22" color="#90A4AE">mdi-chevron-left</v-icon>
        </button>

        <div class="amf-sr-only" aria-hidden="true">
          <v-text-field v-model="name" :rules="[(v) => !!v || $t('is-required')]" />
          <v-text-field
            v-model="currency_amount"
            :rules="[(v) => !!v || $t('is-required')]"
          />
          <autocomplete
            ref="refCurrency"
            v-model="currency"
            :rules="[(v) => !!v || $t('is-required')]"
            :title="$t('currency')"
            type="currency"
            :init="defaultCurrency"
            :value="currency"
            :disabled="id && !$store.state.isMainYear ? true : false"
            @change="changeCurrency"
          />
          <autocomplete
            v-if="type === 'petty'"
            ref="refPettyPerson"
            v-model="pettyPerson"
            title="pettyPerson"
            type="person"
            :filter="true"
            :multiple="true"
            :value="pettyPerson"
          />
        </div>
      </div>

      <!-- Desktop / embedded body -->
      <div v-if="!isMobile || isEmbedded" class="app-form-body">
        <v-container fluid class="app-form-container app-form-container--cards pa-3">
          <v-row class="app-form-card-grid ma-0" dense>
            <v-col cols="12" md="7" class="pa-2">
              <div class="app-form-card">
                <div class="app-form-card__header">
                  <div class="app-form-card__icon" aria-hidden="true">
                    <v-icon color="primary" size="22">{{ typeIcon }}</v-icon>
                  </div>
                  <div class="app-form-card__heading">
                    <div class="app-form-card__title">
                      {{ $t("public-information") }}
                    </div>
                    <div class="app-form-card__subtitle">
                      {{ pageTitle }}
                    </div>
                  </div>
                </div>
                <div
                  class="app-form-card__body app-form-card__body--stack"
                  :class="{ 'app-form-card__body--with-notify': type == 'cash' }"
                >
                  <v-row class="app-form-fields ma-0">
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="name"
                        :label="nameLabel"
                        dense
                        :rules="[(v) => !!v || $t('is-required')]"
                        outlined
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col v-if="type == 'bank'" cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="branch"
                        :label="$t('branch')"
                        dense
                        outlined
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col v-if="type == 'bank'" cols="12" sm="6" md="4">
                      <v-text-field
                        v-model="number"
                        :label="$t('number') + ' ' + $t('account')"
                        dense
                        outlined
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col v-if="type == 'bank'" cols="12" sm="6" md="4">
                      <template>
                        <v-text-field
                          id="fromId"
                          v-model="date"
                          :label="$t('Account opening date')"
                          outlined
                          dense
                          clearable
                          hide-details="auto"
                        />
                        <date-picker
                          v-model="date"
                          element="fromId"
                          :locale="$store.state.date_locale"
                          :format="$store.state.date_format"
                          auto-submit
                          :color="
                            this.$vuetify.theme.themes[
                              this.$vuetify.theme.isDark ? 'dark' : 'light'
                            ].primary
                          "
                          @close="setDate"
                        />
                      </template>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <autocomplete
                        ref="refCurrency"
                        v-model="currency"
                        :rules="[(v) => !!v || $t('is-required')]"
                        :title="$t('currency')"
                        type="currency"
                        :init="defaultCurrency"
                        :value="currency"
                        :disabled="id && !$store.state.isMainYear ? true : false"
                        @change="changeCurrency"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <money
                        v-model="currency_amount"
                        :label="$t('currency-rate')"
                        :rules="[(v) => !!v || $t('is-required')]"
                        :dense="true"
                        :outlined="true"
                        :solo="false"
                        :clearable="true"
                        :disabled="id && !$store.state.isMainYear ? true : false"
                        value-when-is-empty=""
                        :options="currencyOptions"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <money
                        v-model="initial_price"
                        :label="$t('initialPrice')"
                        :dense="true"
                        :outlined="true"
                        :solo="false"
                        :clearable="true"
                        :disabled="!$store.state.isMainYear"
                        value-when-is-empty=""
                        :options="options"
                      />
                    </v-col>
                    <v-col v-if="type == 'petty'" cols="12" sm="6" md="4">
                      <autocomplete
                        v-model="pettyPerson"
                        title="pettyPerson"
                        type="person"
                        :filter="true"
                        :multiple="true"
                        :value="pettyPerson"
                      />
                    </v-col>
                  </v-row>
                  <div v-if="type == 'cash'" class="app-form-card__notify">
                    <div class="app-form-notify mx-0 my-0">
                      <div class="app-form-notify__add app-form-notify__add--pos">
                        <v-btn
                          small
                          outlined
                          color="primary"
                          class="mt-1 app-form-notify__add-btn--pos"
                          @click="addPOS"
                        >
                          <v-icon color="primary" class="ms-1" style="font-size: 22px">
                            mdi-point-of-sale
                          </v-icon>
                          {{ $t("newPOS") }}
                        </v-btn>
                      </div>
                      <div class="app-form-notify__scroll">
                        <div
                          v-for="(item, i) in posItems"
                          :key="i"
                          class="app-form-notify__row"
                        >
                          <v-select
                            v-model="item.type"
                            class="app-form-notify__field"
                            :label="$t('posType')"
                            :items="posTypeItems"
                            dense
                            outlined
                            hide-details
                            item-value="value"
                            item-text="text"
                          />
                          <v-text-field
                            v-model="item.name"
                            class="app-form-notify__field"
                            :label="$t('posName')"
                            dense
                            outlined
                            hide-details
                            clearable
                          />
                          <v-text-field
                            v-model="item.terminal_number"
                            class="app-form-notify__field"
                            :label="$t('terminalNumber')"
                            dense
                            outlined
                            hide-details
                            clearable
                          />
                          <v-text-field
                            v-model="item.username"
                            class="app-form-notify__field"
                            :label="$t('username')"
                            dense
                            outlined
                            hide-details
                            clearable
                          />
                          <v-text-field
                            v-model="item.password"
                            class="app-form-notify__field"
                            :label="$t('password')"
                            dense
                            outlined
                            hide-details
                            clearable
                          />
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                color="pink"
                                icon
                                small
                                v-bind="attrs"
                                class="app-form-notify__remove"
                                v-on="on"
                                @click="removePos(item)"
                              >
                                <v-icon color="pink" size="22">mdi-close</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("remove") }}</span>
                          </v-tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="5" class="pa-2">
              <div class="app-form-card">
                <div class="app-form-card__header">
                  <div class="app-form-card__icon" aria-hidden="true">
                    <v-icon color="primary" size="22">mdi-tune-variant</v-icon>
                  </div>
                  <div class="app-form-card__heading">
                    <div class="app-form-card__title">
                      {{ $t("otherInputs") }}
                    </div>
                    <div class="app-form-card__subtitle">
                      {{ $t("other-inputs-hint") }}
                    </div>
                  </div>
                </div>
                <div class="app-form-card__body app-form-card__body--stack">
                  <v-row class="app-form-fields ma-0">
                    <v-col cols="12" sm="6">
                      <tag v-model="tag" :solo="false" :input="tag" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <autocomplete v-model="given" :title="$t('given')" type="given" />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="custom_code"
                        :label="$t('Custom financial ID')"
                        outlined
                        clearable
                        dense
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" class="app-form-fields__grow">
                      <description
                        v-model="content"
                        class="payment-description"
                        :outlined="true"
                        :rows="4"
                        :value="content"
                        type="sell"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile sheets -->
      <template v-if="isMobile && !isEmbedded">
        <v-bottom-sheet v-model="nameSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ nameLabel }}</div>
              <button type="button" class="amf-sheet__back" @click="nameSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-text-field
                v-model="name"
                :label="nameLabel"
                dense
                :rules="[(v) => !!v || $t('is-required')]"
                outlined
                clearable
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="nameSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="branchSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("branch") }}</div>
              <button type="button" class="amf-sheet__back" @click="branchSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-text-field
                v-model="branch"
                :label="$t('branch')"
                dense
                outlined
                clearable
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="branchSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="numberSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("number") + " " + $t("account") }}</div>
              <button type="button" class="amf-sheet__back" @click="numberSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body">
              <v-text-field
                v-model="number"
                :label="$t('number') + ' ' + $t('account')"
                dense
                outlined
                clearable
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="numberSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="dateSheet">
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("Account opening date") }}</div>
              <button type="button" class="amf-sheet__back" @click="dateSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-mobile-date" dir="rtl">
              <div class="amf-mobile-date__selected">
                <span class="amf-mobile-date__label">{{ $t("Account opening date") }}</span>
                <strong class="amf-mobile-date__value">{{ date || "—" }}</strong>
                <button
                  v-if="date"
                  type="button"
                  class="amf-mobile-date__clear"
                  @click="date = null"
                >
                  {{ $t("clear") }}
                </button>
              </div>
              <date-picker
                v-model="date"
                inline
                simple
                auto-submit
                type="date"
                :locale="$store.state.date_locale"
                :format="$store.state.date_format"
                class="amf-mobile-date__picker"
                color="#7B8BA3"
                @close="setDate"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="dateSheet = false">
                {{ $t("Save") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="otherSheet" eager>
          <v-card class="amf-sheet" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("otherInputs") }}</div>
              <button type="button" class="amf-sheet__back" @click="otherSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-sheet__body--stack">
              <v-checkbox
                v-model="status"
                class="mt-0"
                dense
                hide-details
                :label="$t('status') + '  :  ' + (status ? $t('active') : $t('deactive'))"
                value="active"
              />
              <tag v-model="tag" :solo="false" :input="tag" />
              <autocomplete v-model="given" :title="$t('given')" type="given" />
              <v-text-field
                v-model="custom_code"
                :label="$t('Custom financial ID')"
                outlined
                clearable
                dense
              />
              <description
                v-model="content"
                class="payment-description"
                :outlined="true"
                :rows="4"
                :value="content"
                type="sell"
              />
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="otherSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="posSheet" eager>
          <v-card class="amf-sheet amf-sheet--pos" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-sheet__header">
              <span class="amf-sheet__header-spacer" />
              <div class="amf-sheet__title">{{ $t("newPOS") }}</div>
              <button type="button" class="amf-sheet__back" @click="posSheet = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-sheet__body amf-pos-sheet__body">
              <button type="button" class="amf-pos-sheet__add" @click="addPOS">
                <v-icon size="20" color="primary">mdi-point-of-sale</v-icon>
                <span>{{ $t("newPOS") }}</span>
              </button>

              <div class="amf-pos-sheet__list">
                <div
                  v-if="!posItems || !posItems.length"
                  class="amf-pos-sheet__empty"
                >
                  {{ $t("no-data-found") }}
                </div>

                <div
                  v-for="(item, i) in posItems"
                  :key="'pos-' + i"
                  class="amf-pos-card"
                >
                  <div class="amf-pos-card__head">
                    <div class="amf-pos-card__title">
                      {{ item.name || $t("newPOS") }}
                    </div>
                    <button
                      type="button"
                      class="amf-pos-card__remove"
                      :aria-label="$t('remove')"
                      @click="removePos(item)"
                    >
                      <v-icon size="20" color="#E91E63">mdi-close</v-icon>
                    </button>
                  </div>

                  <v-select
                    v-model="item.type"
                    :label="$t('posType')"
                    :items="posTypeItems"
                    dense
                    outlined
                    hide-details="auto"
                    item-value="value"
                    item-text="text"
                    class="amf-pos-card__field"
                  />
                  <v-text-field
                    v-model="item.name"
                    :label="$t('posName')"
                    dense
                    outlined
                    clearable
                    hide-details="auto"
                    class="amf-pos-card__field"
                  />
                  <v-text-field
                    v-model="item.terminal_number"
                    :label="$t('terminalNumber')"
                    dense
                    outlined
                    clearable
                    hide-details="auto"
                    class="amf-pos-card__field"
                  />
                  <v-text-field
                    v-model="item.username"
                    :label="$t('username')"
                    dense
                    outlined
                    clearable
                    hide-details="auto"
                    class="amf-pos-card__field"
                  />
                  <v-text-field
                    v-model="item.password"
                    :label="$t('password')"
                    dense
                    outlined
                    clearable
                    hide-details="auto"
                    class="amf-pos-card__field"
                  />
                </div>
              </div>
            </div>
            <div class="amf-sheet__footer">
              <button type="button" class="amf-sheet__btn" @click="posSheet = false">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>

        <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
          <v-card class="amf-numpad" dir="rtl">
            <div class="amf-sheet__handle" />
            <div class="amf-numpad__header">
              <span class="amf-numpad__header-spacer" />
              <div class="amf-numpad__title">{{ numpadLabel }}</div>
              <button type="button" class="amf-sheet__back" @click="numpadOpen = false">
                <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
              </button>
            </div>
            <div class="amf-numpad__display">
              <div class="amf-numpad__display-label">{{ numpadLabel }}</div>
              <div class="amf-numpad__display-value">{{ numpadDisplay }}</div>
            </div>
            <div class="amf-numpad__pad" dir="ltr">
              <div class="amf-numpad__digits">
                <button
                  v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
                  :key="'d-' + n"
                  type="button"
                  class="amf-numpad__key"
                  @click="numpadDigit(n)"
                >
                  {{ n }}
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--multi"
                  @click="numpadAppend('00')"
                >
                  00
                </button>
                <button type="button" class="amf-numpad__key" @click="numpadDigit(0)">
                  0
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--multi"
                  @click="numpadAppend('000')"
                >
                  000
                </button>
              </div>
              <div class="amf-numpad__actions">
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--action"
                  @click="numpadStep(1)"
                >
                  +
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--action"
                  @click="numpadStep(-1)"
                >
                  -
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--action"
                  @click="numpadClear"
                >
                  C
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--action"
                  @click="numpadDot"
                >
                  .
                </button>
                <button
                  type="button"
                  class="amf-numpad__key amf-numpad__key--action"
                  @click="numpadBackspace"
                >
                  <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
                </button>
              </div>
            </div>
            <div class="amf-numpad__footer">
              <button type="button" class="amf-sheet__btn" @click="confirmNumpad">
                {{ $t("confirm") }}
              </button>
            </div>
          </v-card>
        </v-bottom-sheet>
      </template>

      <div class="app-form-page__actions">
        <div class="app-form-page__actions-inner">
          <v-btn
            v-if="!id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="create"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="id"
            color="primary"
            depressed
            class="app-form-action-btn"
            :loading="isLoading"
            @click="update"
          >
            {{ $t("submit") }}
          </v-btn>
          <v-btn
            v-if="!formType"
            color="primary"
            outlined
            depressed
            class="app-form-action-btn"
            @click="goTo(type + 'List')"
          >
            {{ $t(type + "List") }}
          </v-btn>
        </div>
      </div>
    </v-form>
    <snakbara :text="snakbarText" :active="snakbar" :color="snakbarColor" />
  </v-card>
</template>

<script>
import General from "../../../../Mixins/general";
import Description from "../../components/general/description/Description";
export default {
  name: "Product",
  components: {
    autocomplete: () => import("../../components/general/patial/autocomplete"),
    Description,
    Tag: () => import("../../components/general/Tag/tag"),
  },
  mixins: [General],
  props: ["formType"],
  data: () => ({
    options: {
      locale: "en-US",
      prefix: "",
      suffix: " ",
      precision: 0,
    },
    currencyOptions: {
      locale: "en-US",
      prefix: "",
      suffix: " ",
      precision: 10,
    },
    posStatus: false,
    render: false,
    isLoading: false,
    alert: false,
    given: null,
    tag: null,
    content: null,
    valid: null,
    posItems: [],
    posTypeItems: null,
    message: null,
    alertType: null,
    name: null,
    id: null,
    currency: null,
    currency_amount: null,
    status: "active",
    number: null,
    type: "bank",
    initial_price: null,
    loading: false,
    pettyPerson: null,
    custom_code: null,
    branch: null,
    date: null,
    userFinancialAccess: {
      user: {
        id: [],
      },
    },
    nameSheet: false,
    branchSheet: false,
    numberSheet: false,
    dateSheet: false,
    otherSheet: false,
    posSheet: false,
    numpadOpen: false,
    numpadField: null,
    numpadLabel: "",
    numpadDraft: "",
    numpadIsAmount: true,
  }),
  computed: {
    isEmbedded() {
      return !!this.formType;
    },
    pageTitle() {
      if (this.type === "bank") return this.$t("bank");
      if (this.type === "cash") return this.$t("cash");
      if (this.type === "petty") return this.$t("petty");
      return "";
    },
    nameLabel() {
      if (this.type === "bank") return this.$t("bank-name");
      if (this.type === "cash") return this.$t("cash-name");
      return this.$t("petty-name");
    },
    typeIcon() {
      if (this.type === "cash") return "mdi-cash-register";
      if (this.type === "petty") return "mdi-wallet-outline";
      return "mdi-bank-outline";
    },
    mobileCurrencyText() {
      if (!this.currency) return "—";
      return this.currency.name || "—";
    },
    mobileRateText() {
      if (
        this.currency_amount === null ||
        this.currency_amount === undefined ||
        this.currency_amount === ""
      ) {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.currency_amount, "plain", 0)
        : String(this.currency_amount);
    },
    mobileInitialText() {
      if (
        this.initial_price === null ||
        this.initial_price === undefined ||
        this.initial_price === ""
      ) {
        return "—";
      }
      return this.$numberFormat
        ? this.$numberFormat(this.initial_price, "plain", 0)
        : String(this.initial_price);
    },
    mobilePettyPersonText() {
      if (!this.pettyPerson) return "";
      const list = Array.isArray(this.pettyPerson)
        ? this.pettyPerson
        : [this.pettyPerson];
      if (!list.length) return "";
      return list
        .map((p) => (p && (p.name || p.title || p.label)) || "")
        .filter(Boolean)
        .join("، ");
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft || "");
      if (!raw) return "0";
      if (!this.numpadIsAmount || !this.$numberFormat) return raw;
      if (raw.endsWith(".")) {
        const head = raw.slice(0, -1);
        return (head ? this.$numberFormat(head, "plain", 0) : "0") + ".";
      }
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return raw;
      return this.$numberFormat(n, "plain", 0);
    },
  },
  watch: {
    defaultCurrency() {
      if (this.defaultCurrency) {
        if (this.$refs.refCurrency) {
          this.$refs.refCurrency.setModel(this.defaultCurrency);
        }
        this.currency_amount = this.defaultCurrency.amount;
      }
    },
    currency(val) {
      if (val) {
        this.currency_amount = val.amount;
        this.options.suffix = val.symbol;
      } else {
        this.options.suffix = " ";
        this.currency_amount = null;
      }
    },
    pettyPerson(val) {
      this.userFinancialAccess.user.id = val;
    },
    isMobile(val) {
      if (this.isEmbedded) return;
      this.lockPageScroll(!!val);
    },
  },
  beforeMount() {
    if (this.$route.params.id != null) {
      this.show();
      this.id = this.$route.params.id;
      this.render = false;
    } else {
      this.render = true;
      this.defaultCurrency = this.getDefault("defaultCurrency");
    }
  },
  mounted() {
    if (this.$route.name == "cash") {
      this.type = "cash";
    }
    if (this.$route.name == "bank") {
      this.type = "bank";
    }
    if (this.$route.name == "petty") {
      this.type = "petty";
    }
    if (this.formType) {
      this.type = this.formType;
    }
    this.posTypeItems = [
      {
        value: "samanKish",
        text: this.$t("samanKish"),
      },
    ];
    if (this.isMobile && !this.isEmbedded) {
      this.lockPageScroll(true);
    }
  },
  beforeDestroy() {
    this.lockPageScroll(false);
  },
  methods: {
    openMobileAutocomplete(refName) {
      const ref = this.$refs[refName];
      if (!ref || typeof ref.openMobileDialog !== "function") return;
      if (ref.disabled) return;
      ref.openMobileDialog();
    },
    lockPageScroll(lock) {
      const value = lock ? "hidden" : "";
      const overscroll = lock ? "none" : "";
      document.documentElement.style.overflow = value;
      document.documentElement.style.overscrollBehavior = overscroll;
      document.body.style.overflow = value;
      document.body.style.overscrollBehavior = overscroll;
      const main = document.querySelector(".v-main");
      const wrap = document.querySelector(".v-main__wrap");
      if (main) {
        main.style.overflow = value;
        main.style.overscrollBehavior = overscroll;
      }
      if (wrap) {
        wrap.style.overflow = value;
        wrap.style.overscrollBehavior = overscroll;
      }
    },
    openRateNumpad() {
      if (this.id && !this.$store.state.isMainYear) return;
      this.numpadField = "currency_amount";
      this.numpadLabel = this.$t("currency-rate");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.currency_amount !== null &&
        this.currency_amount !== undefined &&
        this.currency_amount !== ""
          ? String(this.currency_amount)
          : "";
      this.numpadOpen = true;
    },
    openInitialNumpad() {
      if (!this.$store.state.isMainYear) return;
      this.numpadField = "initial_price";
      this.numpadLabel = this.$t("initialPrice");
      this.numpadIsAmount = true;
      this.numpadDraft =
        this.initial_price !== null &&
        this.initial_price !== undefined &&
        this.initial_price !== ""
          ? String(this.initial_price)
          : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        const parsed = raw === "" ? 0 : parseFloat(raw);
        this[this.numpadField] = Number.isNaN(parsed) ? 0 : parsed;
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    removePos(item) {
      var i = this.posItems.indexOf(item);
      this.posItems.splice(i, 1);
    },
    addPOS() {
      if (!this.posItems) {
        this.posItems = [];
      }
      if (this.posItems.length === 0) {
        var item = {
          name: null,
          type: null,
          terminal_number: null,
          username: null,
          password: null,
        };
        this.posItems.push(item);
      }
    },
    changeCurrency() {
      this.bankRemain = 0;
      this.currency_amount = this.currency ? this.currency.amount : null;
      this.options = {
        locale: "en-US",
        prefix: "",
        suffix: this.currency ? this.currency.symbol : null,
        precision: 0,
      };
    },
    create() {
      var app = this;
      app.alert = false;
      app.isLoading = true;
      app.$axios
        .post(this.$endpoint + "bank", app.$data)
        .then((res) => {
          if (app.id) {
            app.goTo(app.type + "List");
          } else {
            app.$emit("input", res.data.data);
            app.snakbarText = app.$t("registered");
            app.snakbarColor = "green";
            app.snakbar = true;
          }
          app.$emit("close", app.$route);

          app.goTo(app.type + "List");
        })
        .catch((err) => {
          this.serverErrorHandeler(err);
        })
        .finally(() => {
          this.isLoading = false;
          this.clearForm();
        });
    },
    show() {
      var app = this;
      app.loading = true;
      app.clearing = true;
      app.showing = true;
      app.$axios
        .get(this.$endpoint + "bank/" + this.$route.params.id + "/edit", {
          params: {
            id: this.$route.params.id,
          },
        })
        .then((res) => {
          res = res.data;
          app.$nextTick(() => {
            app.type = res.type;
            app.typeForm = res.type;
            app.id = res.id;
            app.name = res.name;
            app.branch = res.branch;
            app.date = res.date;
            app.status = res.status;
            app.given = res.given;
            app.custom_code = res?.financial?.custom_code;
            app.number = res.number;
            app.pettyPerson =
              this.type == "petty" ? res.user_financial.map((item) => item.user) : null;
            app.posItems = res.pos ? [res.pos] : null;
            app.initial_price = res.initial_price;
            app.currency = res.currency;
            app.currency_amount = res.currency_amount;
            app.content = res.content;
            if (res.tags) {
              app.tag = res.tags;
            }
            app.render = true;
          });
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.loading = false));
    },
    update() {
      var app = this;
      app.isLoading = true;
      app.$axios
        .put(this.$endpoint + "bank/" + this.$route.params.id, this.$data)
        .then((res) => {
          res = res.data;
          app.$emit("close", app.$route);
          app.goTo(app.type + "List");
        })
        .catch((err) => {
          app.serverErrorHandeler(err);
        })
        .finally(() => (app.isLoading = false));
    },
    clearForm() {
      this.tag = null;
      this.content = null;
      this.name = null;
      this.snakbar = false;
      this.id = null;
      this.status = "active";
      this.number = null;
      this.initial_price = null;
      this.given = null;
      this.currency = null;
      this.pettyPerson = null;
      this.custom_code = null;
      this.$refs.form.reset();
    },
  },
};
</script>

<style scoped></style>
